import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function renderMarkdown(text) {
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const bold = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith("**") && p.endsWith("**") ? <strong key={j} className="text-[#1A231F]">{p.slice(2, -2)}</strong> : p);
    if (line.startsWith("## ")) return <h4 key={i} className="font-heading font-bold text-[#1A231F] text-base mt-3 mb-1">{line.slice(3)}</h4>;
    if (/^\s*[-*]\s/.test(line)) return <li key={i} className="ml-4 list-disc text-[#3A463F] leading-relaxed">{bold(line.replace(/^\s*[-*]\s/, ""))}</li>;
    if (line.trim() === "") return <div key={i} className="h-2" />;
    return <p key={i} className="text-[#3A463F] leading-relaxed">{bold(line)}</p>;
  });
}

export const ToolAdvisor = ({ tool, title, context }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    setText("");
    try {
      const res = await fetch(`${API}/advisor/tool`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tool, title, context }),
      });
      if (!res.ok || !res.body) throw new Error("advisor failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setText((t) => t + dec.decode(value));
      }
    } catch {
      setText("Sorry, the advisor is unavailable right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#1E2B25] rounded-2xl p-5 sm:p-6 shadow-sm" data-testid="tool-advisor">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-white">
          <Sparkles className="h-5 w-5 text-[#D99B26]" />
          <h3 className="font-heading text-lg font-bold">AI Advisor</h3>
        </div>
        <Button data-testid="tool-advice-btn" onClick={generate} disabled={loading}
          className="rounded-full bg-[#D99B26] hover:bg-[#c48a1f] text-[#1A231F] font-semibold">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Thinking…</> : "Get advice"}
        </Button>
      </div>
      <AnimatePresence>
        {(text || loading) && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
            className="mt-4 bg-white rounded-xl p-4 text-sm" data-testid="tool-advice-content">
            {text ? <div className="space-y-0.5">{renderMarkdown(text)}</div>
              : <div className="flex items-center gap-2 text-[#8A9790]"><Loader2 className="h-4 w-4 animate-spin" /> Analysing…</div>}
          </motion.div>
        )}
      </AnimatePresence>
      {!text && !loading && (
        <p className="text-white/55 text-sm mt-3">Get a personalised, plain-English read on your numbers.</p>
      )}
    </div>
  );
};
